import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import MaterialTextField from '@mui/material/TextField';
import MaterialCheckbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { makeStyles } from 'tss-react/mui';
import ConfirmButton from '../confirm-button';
import { meQuery } from '../../apollo/auth';
import { requestJoinGroupMutation } from '../../apollo/groups';
import type { Group } from '../../types/Group';
import { useAppContext } from '../../contexts/app';
import Markdown from '../markdown';

const useStyles = makeStyles()((theme) => ({
  formControl: {
    display: 'block',
    margin: theme.spacing(1)
  },
  helperText: {
    paddingLeft: theme.spacing(4),
    marginTop: 0
  },
  prompt: {
    '& > *:first-of-type': {
      marginTop: 0
    }
  }
}));

type Props = {
  group?: Group;
  returnTo?: string;
  onJoin?: (...args: unknown[]) => void;
};

const RequestJoinButton = ({ group, returnTo, onJoin }: Props) => {
  const { isLoggedIn, loggedInUser, login } = useAppContext();

  const [requestJoinGroup] = useMutation(requestJoinGroupMutation, {
    update: (proxy, { data: { requestJoinGroup: groupMember } }) => {
      const data = proxy.readQuery({
        query: meQuery
      });
      proxy.writeQuery({
        query: meQuery,
        data: {
          ...data,
          me: {
            ...data.me,
            groupMembers: [...data.me.groupMembers, groupMember]
          }
        }
      });
    }
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [membership, setMembership] = useState({
    phoneNumberVisibility: 'NONE',
    emailVisibility: 'NONE'
  });
  const { classes } = useStyles();

  if (!isLoggedIn) {
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={(e) => {
          e.preventDefault();
          login(returnTo || `/groups/${group.slug}`);
        }}
      >
        Join Group
      </Button>
    );
  }

  return (
    <ConfirmButton
      label="Request to Join Group"
      message="Request to Join Group"
      variant="contained"
      color="primary"
      onClick={() => {
        setSubmitting(true);
        requestJoinGroup({
          variables: {
            id: group.id,
            message,
            membership
          }
        }).then(() => {
          setSubmitting(false);
          if (onJoin) {
            onJoin();
          }
        }).catch(() => {
          alert('An error occurred while requesting. Please try again.');
          setSubmitting(false);
        });
      }}
      disabled={submitting}
    >
      <DialogContentText className={classes.prompt}>
        <Markdown>{group.requestPrompt || 'Include a message to the group admin.'}</Markdown>
      </DialogContentText>
      <MaterialTextField
        autoFocus
        id="message"
        label="Message to Group Admins"
        fullWidth
        multiline
        rows={3}
        maxRows={20}
        variant="outlined"
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
        }}
        disabled={submitting}
      />
      <FormControl className={classes.formControl}>
        <FormControlLabel
          control={(
            <MaterialCheckbox
              id="emailVisibility"
              onChange={(e) => {
                setMembership({
                  ...membership,
                  emailVisibility: e.target.checked ? 'ADMIN' : 'NONE'
                });
              }}
              value={true}
              checked={membership.emailVisibility === 'ADMIN'}
            />
          )}
          label="Share email with group admins"
          disabled={submitting}
        />
        <FormHelperText className={classes.helperText}>
          {loggedInUser.email}
        </FormHelperText>
      </FormControl>
      <FormControl className={classes.formControl}>
        <FormControlLabel
          control={(
            <MaterialCheckbox
              id="phoneNumberVisibility"
              onChange={(e) => {
                setMembership({
                  ...membership,
                  phoneNumberVisibility: e.target.checked ? 'ADMIN' : 'NONE'
                });
              }}
              value={true}
              checked={membership.phoneNumberVisibility === 'ADMIN'}
            />
          )}
          label="Share phone number with group admins"
          disabled={submitting}
        />
        <FormHelperText className={classes.helperText}>
          {loggedInUser.phoneNumberDisplay || 'None set (add in account preferences)'}
        </FormHelperText>
      </FormControl>
    </ConfirmButton>
  );
};

export default RequestJoinButton;
